import { useState, useMemo, useEffect, useLayoutEffect, useRef } from "react";

import CentralCatalogProvider from "./CentralCatalogProvider";
import CentralCatalogParametersPopup from "./CentralCatalogParametersPopup";
import ButtonIconH from "../../components/ButtonIconH";
import Dropdown from "../../components/Dropdown";
import InputData from "../../components/InputData";
import InputDataControl from "./InputDataControl";
import TooltipBox from "../../components/Tooltip";
import DataTable from "react-data-table-component";
import CheckBox from "../../components/CheckBox";
import { formatCurrency } from "../utils/utils";
import { post, postgz } from "../../js/post";
import { reformatField } from "../../js/format";
import { CENTRAL_CATALOG_PROVIDERS } from "../js/constants";
import { userData, selectedOptic } from "../../js/utils";

import "../css/CentralCatalogPopup.css";

const applyProductFilters = (
  products,
  {
    nombre = "",
    tipo,
    tipoLente,
    material,
    geometria,
    masa,
    indDesde = "",
    indHasta = "",
    denDesde = "",
    denHasta = "",
  },
) => {
  const words = nombre.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const indDesdeN = indDesde !== "" ? parseFloat(indDesde) : null;
  const indHastaN = indHasta !== "" ? parseFloat(indHasta) : null;
  const denDesdeN = denDesde !== "" ? parseFloat(denDesde) : null;
  const denHastaN = denHasta !== "" ? parseFloat(denHasta) : null;
  return products.filter((p) => {
    if (
      words.length > 0 &&
      !words.every((w) => p.nombre.toLowerCase().includes(w))
    )
      return false;
    if (tipo !== undefined && tipo?.value && p.tipo !== tipo.value)
      return false;
    if (
      tipoLente !== undefined &&
      tipoLente?.value !== "" &&
      tipoLente?.value != null &&
      p.tipoLente !== tipoLente.value
    )
      return false;
    if (
      material !== undefined &&
      material?.value !== "" &&
      material?.value != null &&
      p.material !== material.value
    )
      return false;
    if (
      geometria !== undefined &&
      geometria?.value &&
      p.geometria !== geometria.value
    )
      return false;
    if (masa !== undefined && masa?.value && p.masa !== masa.value)
      return false;
    if (indDesdeN !== null && p.indice < indDesdeN) return false;
    if (indHastaN !== null && p.indice > indHastaN) return false;
    if (denDesdeN !== null && p.densidad < denDesdeN) return false;
    if (denHastaN !== null && p.densidad > denHastaN) return false;
    return true;
  });
};

export default ({
  show,
  onClose,
  onCargar,
  ojo,
  initialFilters = {},
  createPopupQuestion,
  removePopupQuestion,
}) => {
  const [catalogueData, setCatalogueData] = useState(null);

  const allMasas = useMemo(() => {
    if (!catalogueData) return [{ value: "", text: "" }];
    return [
      { value: "", text: "" },
      ...Array.from(
        new Map(
          catalogueData.Lentes.map((l) => [
            l.Producto.Masa.Valor,
            l.Producto.Masa,
          ]),
        ).values(),
      )
        .sort((a, b) => (a.Valor > b.Valor ? 1 : -1))
        .map((m) => ({ value: m.Valor, text: m.Descripcion.toUpperCase() })),
    ];
  }, [catalogueData]);

  const allGeometrias = useMemo(() => {
    if (!catalogueData) return [{ value: "", text: "" }];
    return [
      { value: "", text: "" },
      ...Array.from(
        new Map(
          catalogueData.Lentes.map((l) => [
            l.Producto.Geometria.Valor,
            l.Producto.Geometria,
          ]),
        ).values(),
      )
        .sort((a, b) => (a.Valor > b.Valor ? 1 : -1))
        .map((g) => ({ value: g.Valor, text: g.Descripcion.toUpperCase() })),
    ];
  }, [catalogueData]);

  const allMateriales = useMemo(() => {
    if (!catalogueData) return [{ value: "", text: "" }];
    return [
      { value: "", text: "" },
      ...Array.from(
        new Map(
          catalogueData.Lentes.map((l) => [
            l.Producto.Material.Valor,
            l.Producto.Material,
          ]),
        ).values(),
      )
        .sort((a, b) => a.Valor - b.Valor)
        .map((m) => ({ value: m.Valor, text: m.Descripcion.toUpperCase() })),
    ];
  }, [catalogueData]);

  const allTiposLente = useMemo(() => {
    if (!catalogueData) return [{ value: "", text: "" }];
    return [
      { value: "", text: "" },
      ...Array.from(
        new Map(
          catalogueData.Lentes.map((l) => [
            l.Producto.TipoLente.Valor,
            l.Producto.TipoLente,
          ]),
        ).values(),
      )
        .sort((a, b) => a.Valor - b.Valor)
        .map((t) => ({ value: t.Valor, text: t.Descripcion.toUpperCase() })),
    ];
  }, [catalogueData]);

  const allLentes = useMemo(() => {
    if (!catalogueData) return [];
    return catalogueData.Lentes.flatMap((l) =>
      l.Producto.Variantes.map((v, vi) => ({
        id: `${l.Producto.CodigoProducto}-${vi}`,
        diametro: v.Diametro,
        cilDesde: v.CilDesde,
        cilHasta: v.CilHasta,
        esfDesde: v.EsfDesde,
        esfHasta: v.EsfHasta,
        adiDesde: v.AdiDesde,
        adiHasta: v.AdiHasta,
        pvp: v.PVP,
        selected: false,
      })),
    );
  }, [catalogueData]);

  const allProducts = useMemo(() => {
    if (!catalogueData) return [];
    return catalogueData.Lentes.map((l) => ({
      id: l.Producto.CodigoProducto,
      codigoProducto: l.Producto.CodigoProducto,
      nombre: l.Producto.Nombre,
      tipo: l.Producto.Tipo,
      tipoLente: l.Producto.TipoLente.Valor,
      material: l.Producto.Material.Valor,
      geometria: l.Producto.Geometria.Valor,
      masa: l.Producto.Masa.Valor,
      indice: l.Producto.IndiceRefraccion,
      densidad: l.Producto.Densidad,
      selected: false,
    }));
  }, [catalogueData]);

  const allDiaOptions = useMemo(() => {
    const diaSet = new Set();
    allLentes.forEach((l) => {
      if (l.diametro != null && l.diametro !== "")
        diaSet.add(String(l.diametro));
    });
    return [
      { value: "", text: "" },
      ...[...diaSet].sort().map((d) => ({ id: d, value: d, text: d })),
    ];
  }, [allLentes]);

  const loadCatalogue = async () => {
    const data = await postgz("ObtenerCatalogosGafasHoya.php", {
      token: userData.token,
      idOptica: selectedOptic.id,
    });
    if (!data) return;
    setCatalogueData(data);
  };

  const [selectedProvider, setSelectedProvider] = useState(null);
  const [providerDates, setProviderDates] = useState({});
  const [productsTable, setProductsTable] = useState([]);
  const [lentesTable, setLentesTable] = useState([]);
  const [nombreFilter, setNombreFilter] = useState("");
  const [tipoFilterSel, setTipoFilterSel] = useState({ value: "", text: "" });
  const [tipoLenteFilterSel, setTipoLenteFilterSel] = useState({
    value: "",
    text: "",
  });
  const [materialFilterSel, setMaterialFilterSel] = useState({
    value: "",
    text: "",
  });
  const [geometriaFilterSel, setGeometriaFilterSel] = useState({
    value: "",
    text: "",
  });
  const [masaFilterSel, setMasaFilterSel] = useState({ value: "", text: "" });
  const [indDesde, setIndDesde] = useState("");
  const [indHasta, setIndHasta] = useState("");
  const [denDesde, setDenDesde] = useState("");
  const [denHasta, setDenHasta] = useState("");
  const [diaOptions, setDiaOptions] = useState([{ value: "", text: "" }]);
  const [diaDesde, setDiaDesde] = useState({ value: "", text: "" });
  const [cilDesde, setCilDesde] = useState("");
  const [esfDesde, setEsfDesde] = useState("");
  const [adiSel, setAdiSel] = useState("");
  const [coloresOptions, setColoresOptions] = useState([
    { value: "", text: "" },
  ]);
  const [colorSel, setColorSel] = useState({ value: "", text: "" });
  const [tratamientosOptions, setTratamientosOptions] = useState([
    { value: "", text: "" },
  ]);
  const [tratamientoSel, setTratamientoSel] = useState({ value: "", text: "" });
  const [instruccionesEspecialesOptions, setInstruccionesEspecialesOptions] =
    useState([{ value: "", text: "" }]);
  const [instruccionEspecialSel, setInstruccionEspecialSel] = useState({
    value: "",
    text: "",
  });
  const [instruccionesEspecialesTable, setInstruccionesEspecialesTable] =
    useState([]);
  const [instruccionesMontajeOptions, setInstruccionesMontajeOptions] =
    useState([{ value: "", text: "" }]);
  const [instruccionMontajeSel, setInstruccionMontajeSel] = useState({
    value: "",
    text: "",
  });
  const [instruccionesMontajeTable, setInstruccionesMontajeTable] = useState(
    [],
  );
  const [showParametersPopup, setShowParametersPopup] = useState(false);
  const [parametrosPopup, setParametrosPopup] = useState([]);

  const productsColumnRef = useRef(null);
  const [suplementosColumnHeight, setSuplementosColumnHeight] =
    useState(null);

  useLayoutEffect(() => {
    if (!selectedProvider) return;
    const el = productsColumnRef.current;
    if (!el) return;
    const updateHeight = () => setSuplementosColumnHeight(el.offsetHeight);
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(el);
    return () => observer.disconnect();
  }, [selectedProvider]);

  const loadProviderDates = async () => {
    const response = await post("ObtenerCatalogosGafas.php", {
      token: userData.token,
      idOptica: selectedOptic.id,
    });
    if (!response) return;
    const dates = {};
    Object.entries(response).forEach(([key, value]) => {
      if (value && String(value).length === 8) {
        const v = String(value);
        dates[key] = `${v.slice(6, 8)}/${v.slice(4, 6)}/${v.slice(0, 4)}`;
      }
    });
    setProviderDates(dates);
  };

  useEffect(() => {
    if (allProducts.length === 0) return;
    setProductsTable(allProducts);
    setDiaOptions(allDiaOptions);
  }, [allProducts]);

  useEffect(() => {
    if (!show) return;
    setSelectedProvider(null);
    loadProviderDates();
    if (allProducts.length > 0) setProductsTable(allProducts);
    setLentesTable([]);
    setNombreFilter("");
    setTipoFilterSel({ value: "", text: "" });
    setTipoLenteFilterSel({ value: "", text: "" });
    setMaterialFilterSel({ value: "", text: "" });
    setGeometriaFilterSel({ value: "", text: "" });
    setMasaFilterSel({ value: "", text: "" });
    setIndDesde("");
    setIndHasta("");
    setDenDesde("");
    setDenHasta("");
    setDiaOptions(allDiaOptions);
    setDiaDesde({ value: "", text: "" });
    setCilDesde("");
    setEsfDesde("");
    setAdiSel("");
    setColoresOptions([{ value: "", text: "" }]);
    setColorSel({ value: "", text: "" });
    setTratamientosOptions([{ value: "", text: "" }]);
    setTratamientoSel({ value: "", text: "" });
    setInstruccionesEspecialesOptions([{ value: "", text: "" }]);
    setInstruccionEspecialSel({ value: "", text: "" });
    setInstruccionesEspecialesTable([]);
    setInstruccionesMontajeOptions([{ value: "", text: "" }]);
    setInstruccionMontajeSel({ value: "", text: "" });
    setInstruccionesMontajeTable([]);
    const { ind, cil, esf, add, dia } = initialFilters;
    if (ind != null && ind !== "") {
      setIndDesde(String(ind));
      setIndHasta(String(ind));
    }
    if (cil != null && cil !== "") setCilDesde(String(cil));
    if (esf != null && esf !== "") setEsfDesde(String(esf));
    if (add != null && add !== "") setAdiSel(String(add));
    if (dia != null && dia !== "") {
      const diaStr = String(dia);
      setDiaDesde({ id: diaStr, value: diaStr, text: diaStr });
    }
  }, [show]);

  const baseFilters = {
    nombre: nombreFilter,
    indDesde,
    indHasta,
    denDesde,
    denHasta,
  };

  const filteredProductsTable = useMemo(() => {
    const base = applyProductFilters(productsTable, {
      ...baseFilters,
      tipo: tipoFilterSel,
      tipoLente: tipoLenteFilterSel,
      material: materialFilterSel,
      geometria: geometriaFilterSel,
      masa: masaFilterSel,
    });
    const cilN = cilDesde !== "" ? parseFloat(cilDesde) : null;
    const esfN = esfDesde !== "" ? parseFloat(esfDesde) : null;
    const adiN = adiSel !== "" ? parseFloat(adiSel) : null;
    const diaStr = (diaDesde?.value ?? "").trim();
    if (cilN === null && esfN === null && adiN === null && diaStr === "")
      return base;
    return base.filter((p) => {
      const variants = allLentes.filter((l) => l.id.startsWith(p.id + "-"));
      return variants.some((v) => {
        if (cilN !== null && (cilN < v.cilDesde || cilN > v.cilHasta))
          return false;
        if (esfN !== null && (esfN < v.esfDesde || esfN > v.esfHasta))
          return false;
        if (adiN !== null && (adiN < v.adiDesde || adiN > v.adiHasta))
          return false;
        if (
          diaStr !== "" &&
          v.diametro != null &&
          v.diametro !== "" &&
          !String(v.diametro).includes(diaStr)
        )
          return false;
        return true;
      });
    });
  }, [
    productsTable,
    nombreFilter,
    tipoFilterSel,
    tipoLenteFilterSel,
    materialFilterSel,
    geometriaFilterSel,
    masaFilterSel,
    indDesde,
    indHasta,
    denDesde,
    denHasta,
    cilDesde,
    esfDesde,
    adiSel,
    diaDesde?.value,
  ]);

  const tipoOptions = useMemo(() => {
    const vals = [
      ...new Set(
        applyProductFilters(productsTable, {
          ...baseFilters,
          tipoLente: tipoLenteFilterSel,
          material: materialFilterSel,
          geometria: geometriaFilterSel,
          masa: masaFilterSel,
        }).map((p) => p.tipo),
      ),
    ]
      .filter(Boolean)
      .sort();
    return [
      { value: "", text: "" },
      ...vals.map((v) => ({ value: v, text: v.toUpperCase() })),
    ];
  }, [
    productsTable,
    nombreFilter,
    tipoLenteFilterSel,
    materialFilterSel,
    geometriaFilterSel,
    masaFilterSel,
    indDesde,
    indHasta,
    denDesde,
    denHasta,
  ]);

  const tipoLenteOptions = useMemo(() => {
    const filtered = applyProductFilters(productsTable, {
      ...baseFilters,
      tipo: tipoFilterSel,
      material: materialFilterSel,
      geometria: geometriaFilterSel,
      masa: masaFilterSel,
    });
    return [
      { value: "", text: "" },
      ...allTiposLente.filter(
        (t) => t.value !== "" && filtered.some((p) => p.tipoLente === t.value),
      ),
    ];
  }, [
    productsTable,
    nombreFilter,
    tipoFilterSel,
    materialFilterSel,
    geometriaFilterSel,
    masaFilterSel,
    indDesde,
    indHasta,
    denDesde,
    denHasta,
  ]);

  const materialOptions = useMemo(() => {
    const filtered = applyProductFilters(productsTable, {
      ...baseFilters,
      tipo: tipoFilterSel,
      tipoLente: tipoLenteFilterSel,
      geometria: geometriaFilterSel,
      masa: masaFilterSel,
    });
    return [
      { value: "", text: "" },
      ...allMateriales.filter(
        (m) => m.value !== "" && filtered.some((p) => p.material === m.value),
      ),
    ];
  }, [
    productsTable,
    nombreFilter,
    tipoFilterSel,
    tipoLenteFilterSel,
    geometriaFilterSel,
    masaFilterSel,
    indDesde,
    indHasta,
    denDesde,
    denHasta,
  ]);

  const geometriaOptions = useMemo(() => {
    const vals = [
      ...new Set(
        applyProductFilters(productsTable, {
          ...baseFilters,
          tipo: tipoFilterSel,
          tipoLente: tipoLenteFilterSel,
          material: materialFilterSel,
          masa: masaFilterSel,
        }).map((p) => p.geometria),
      ),
    ]
      .filter(Boolean)
      .sort();
    return [
      { value: "", text: "" },
      ...allGeometrias.filter((g) => g.value !== "" && vals.includes(g.value)),
    ];
  }, [
    productsTable,
    nombreFilter,
    tipoFilterSel,
    tipoLenteFilterSel,
    materialFilterSel,
    masaFilterSel,
    indDesde,
    indHasta,
    denDesde,
    denHasta,
  ]);

  const masaOptions = useMemo(() => {
    const vals = [
      ...new Set(
        applyProductFilters(productsTable, {
          ...baseFilters,
          tipo: tipoFilterSel,
          tipoLente: tipoLenteFilterSel,
          material: materialFilterSel,
          geometria: geometriaFilterSel,
        }).map((p) => p.masa),
      ),
    ]
      .filter(Boolean)
      .sort();
    return [
      { value: "", text: "" },
      ...allMasas.filter((m) => m.value !== "" && vals.includes(m.value)),
    ];
  }, [
    productsTable,
    nombreFilter,
    tipoFilterSel,
    tipoLenteFilterSel,
    materialFilterSel,
    geometriaFilterSel,
    indDesde,
    indHasta,
    denDesde,
    denHasta,
  ]);

  useEffect(() => {
    if (
      tipoFilterSel?.value &&
      !tipoOptions.some((o) => o.value === tipoFilterSel.value)
    )
      setTipoFilterSel(tipoOptions[0]);
  }, [tipoOptions]);

  useEffect(() => {
    if (
      tipoLenteFilterSel?.value !== "" &&
      tipoLenteFilterSel?.value != null &&
      !tipoLenteOptions.some((o) => o.value === tipoLenteFilterSel.value)
    )
      setTipoLenteFilterSel(tipoLenteOptions[0]);
  }, [tipoLenteOptions]);

  useEffect(() => {
    if (
      materialFilterSel?.value !== "" &&
      materialFilterSel?.value != null &&
      !materialOptions.some((o) => o.value === materialFilterSel.value)
    )
      setMaterialFilterSel(materialOptions[0]);
  }, [materialOptions]);

  useEffect(() => {
    if (
      geometriaFilterSel?.value &&
      !geometriaOptions.some((o) => o.value === geometriaFilterSel.value)
    )
      setGeometriaFilterSel(geometriaOptions[0]);
  }, [geometriaOptions]);

  useEffect(() => {
    if (
      masaFilterSel?.value &&
      !masaOptions.some((o) => o.value === masaFilterSel.value)
    )
      setMasaFilterSel(masaOptions[0]);
  }, [masaOptions]);


  const filteredLentesTable = useMemo(() => {
    const diaStr = (diaDesde?.value ?? "").trim();
    const cilN = cilDesde !== "" ? parseFloat(cilDesde) : null;
    const esfN = esfDesde !== "" ? parseFloat(esfDesde) : null;
    const adiN = adiSel !== "" ? parseFloat(adiSel) : null;
    return lentesTable.filter((l) => {
      if (
        diaStr !== "" &&
        l.diametro !== "" &&
        l.diametro != null &&
        !String(l.diametro).includes(diaStr)
      )
        return false;
      if (cilN !== null && (cilN < l.cilDesde || cilN > l.cilHasta))
        return false;
      if (esfN !== null && (esfN < l.esfDesde || esfN > l.esfHasta))
        return false;
      if (adiN !== null && (adiN < l.adiDesde || adiN > l.adiHasta))
        return false;
      return true;
    });
  }, [lentesTable, diaDesde?.value, cilDesde, esfDesde, adiSel]);

  const totalLentes = useMemo(() => {
    const sel = lentesTable.find((l) => l.selected);
    return sel ? sel.pvp : 0;
  }, [lentesTable]);

  const totalTratamientos = useMemo(
    () =>
      (colorSel?.pvp ?? 0) +
      (tratamientoSel?.pvp ?? 0) +
      instruccionesEspecialesTable.reduce((sum, i) => sum + (i.pvp ?? 0), 0) +
      instruccionesMontajeTable.reduce((sum, i) => sum + (i.pvp ?? 0), 0),
    [
      colorSel,
      tratamientoSel,
      instruccionesEspecialesTable,
      instruccionesMontajeTable,
    ],
  );

  const total = useMemo(
    () => totalLentes + totalTratamientos,
    [totalLentes, totalTratamientos],
  );

  const selectLente = (id) => {
    const tableN = structuredClone(lentesTable);
    tableN.forEach((item) => {
      item.selected = item.id === id ? !item.selected : false;
    });
    setLentesTable(tableN);
  };

  const lentesColumns = useMemo(
    () => [
      {
        name: "Diá",
        selector: (row) => row.diametro,
        sortable: true,
        sortFunction: (a, b) =>
          String(a.diametro ?? "").localeCompare(String(b.diametro ?? "")),
        width: "6%",
      },
      {
        name: "Cil (Desde)",
        selector: (row) => reformatField(String(row.cilDesde), "type-optofloat"),
        sortable: true,
        sortFunction: (a, b) => a.cilDesde - b.cilDesde,
        width: "13%",
        right: true,
      },
      {
        name: "Cil (Hasta)",
        selector: (row) => reformatField(String(row.cilHasta), "type-optofloat"),
        sortable: true,
        sortFunction: (a, b) => a.cilHasta - b.cilHasta,
        width: "13%",
        right: true,
      },
      {
        name: "Esf (Desde)",
        selector: (row) => reformatField(String(row.esfDesde), "type-optofloat"),
        sortable: true,
        sortFunction: (a, b) => a.esfDesde - b.esfDesde,
        width: "13%",
        right: true,
      },
      {
        name: "Esf (Hasta)",
        selector: (row) => reformatField(String(row.esfHasta), "type-optofloat"),
        sortable: true,
        sortFunction: (a, b) => a.esfHasta - b.esfHasta,
        width: "13%",
        right: true,
      },
      {
        name: "Add (Desde)",
        selector: (row) =>
          reformatField(String(row.adiDesde), "type-positive-optofloat"),
        sortable: true,
        sortFunction: (a, b) => a.adiDesde - b.adiDesde,
        width: "13%",
        right: true,
      },
      {
        name: "Add (Hasta)",
        selector: (row) =>
          reformatField(String(row.adiHasta), "type-positive-optofloat"),
        sortable: true,
        sortFunction: (a, b) => a.adiHasta - b.adiHasta,
        width: "13%",
        right: true,
      },
      {
        name: "PVP",
        selector: (row) => formatCurrency(row.pvp),
        sortable: true,
        sortFunction: (a, b) => a.pvp - b.pvp,
        width: "12%",
        right: true,
      },
      {
        name: "",
        sortable: false,
        width: "4%",
        cell: (row) => (
          <div
            className="data-field c-pointer"
            onClick={(e) => {
              e.stopPropagation();
              selectLente(row.id);
            }}
          >
            <CheckBox active={row.selected} onClick={() => {}} />
          </div>
        ),
      },
    ],
    [lentesTable],
  );

  const selectProduct = (id) => {
    const tableN = structuredClone(productsTable);
    let nowSelected = false;
    tableN.forEach((item) => {
      item.selected = item.id === id ? !item.selected : false;
      if (item.id === id && item.selected) nowSelected = true;
    });
    setProductsTable(tableN);
    setLentesTable(
      nowSelected ? allLentes.filter((l) => l.id.startsWith(id + "-")) : [],
    );
    if (nowSelected) {
      const lente = catalogueData?.Lentes.find(
        (l) => l.Producto.CodigoProducto === id,
      );
      const diaSet = new Set();
      (lente?.Producto.Diametros ?? []).forEach(
        ({ GrupoDiametro, Diametros }) => {
          diaSet.add(GrupoDiametro);
          Diametros.forEach((d) => diaSet.add(d));
        },
      );
      setDiaOptions([
        { value: "", text: "" },
        ...[...diaSet].sort().map((d) => ({ id: d, value: d, text: d })),
      ]);
      const colores = (lente?.Producto.Colores ?? [])
        .slice()
        .sort((a, b) => a.CodigoSuplemento.localeCompare(b.CodigoSuplemento));
      setColoresOptions([
        { value: "", text: "" },
        ...colores.map((c) => ({
          value: c.CodigoSuplemento,
          text: `${c.CodigoSuplemento.toUpperCase()} - ${c.Nombre.toUpperCase()} (${formatCurrency(c.PVP)})`,
          pvp: c.PVP,
          nombre: c.Nombre,
        })),
      ]);
      const tratamientos = (lente?.Producto.Tratamientos ?? [])
        .slice()
        .sort((a, b) => a.CodigoSuplemento.localeCompare(b.CodigoSuplemento));
      setTratamientosOptions([
        { value: "", text: "" },
        ...tratamientos.map((t) => ({
          value: t.CodigoSuplemento,
          text: `${t.CodigoSuplemento.toUpperCase()} - ${t.Nombre.toUpperCase()} (${formatCurrency(t.PVP)})`,
          pvp: t.PVP,
          nombre: t.Nombre,
        })),
      ]);
      const instruccionesEspeciales = (
        lente?.Producto.InstruccionesEspeciales ?? []
      )
        .slice()
        .sort((a, b) => a.CodigoSuplemento.localeCompare(b.CodigoSuplemento));
      setInstruccionesEspecialesOptions([
        { value: "", text: "" },
        ...instruccionesEspeciales.map((i) => ({
          value: i.CodigoSuplemento,
          text: `${i.CodigoSuplemento.toUpperCase()} - ${i.Nombre.toUpperCase()} (${formatCurrency(i.PVP)})`,
          pvp: i.PVP,
          nombre: i.Nombre,
        })),
      ]);
      setInstruccionesEspecialesTable([]);
      const instruccionesMontaje = (lente?.Producto.InstruccionesMontaje ?? [])
        .slice()
        .sort((a, b) => a.CodigoSuplemento.localeCompare(b.CodigoSuplemento));
      setInstruccionesMontajeOptions([
        { value: "", text: "" },
        ...instruccionesMontaje.map((i) => ({
          value: i.CodigoSuplemento,
          text: `${i.CodigoSuplemento.toUpperCase()} - ${i.Nombre.toUpperCase()} (${formatCurrency(i.PVP)})`,
          pvp: i.PVP,
          nombre: i.Nombre,
        })),
      ]);
      setInstruccionesMontajeTable([]);
    } else {
      setDiaOptions(allDiaOptions);
      setColoresOptions([{ value: "", text: "" }]);
      setTratamientosOptions([{ value: "", text: "" }]);
      setInstruccionesEspecialesOptions([{ value: "", text: "" }]);
      setInstruccionesEspecialesTable([]);
      setInstruccionesMontajeOptions([{ value: "", text: "" }]);
      setInstruccionesMontajeTable([]);
    }
    setDiaDesde({ value: "", text: "" });
    setColorSel({ value: "", text: "" });
    setTratamientoSel({ value: "", text: "" });
    setInstruccionEspecialSel({ value: "", text: "" });
    setInstruccionMontajeSel({ value: "", text: "" });
  };

  const selectedProductId = useMemo(
    () => productsTable.find((p) => p.selected)?.codigoProducto ?? null,
    [productsTable],
  );

  const allActiveCodes = useMemo(() => {
    const codes = new Set();
    if (colorSel?.value) codes.add(colorSel.value);
    if (tratamientoSel?.value) codes.add(tratamientoSel.value);
    instruccionesEspecialesTable.forEach((i) => codes.add(i.id));
    instruccionesMontajeTable.forEach((i) => codes.add(i.id));
    return codes;
  }, [
    colorSel,
    tratamientoSel,
    instruccionesEspecialesTable,
    instruccionesMontajeTable,
  ]);

  const blockedCodes = useMemo(() => {
    const blocked = new Set();
    (catalogueData?.Incompatibilidades ?? []).forEach(
      ({ CodigoSup1, CodigoSup2, CodigosProducto }) => {
        if (!CodigoSup2) return;
        const applies =
          CodigosProducto.length === 0 ||
          (selectedProductId && CodigosProducto.includes(selectedProductId));
        if (!applies) return;
        if (allActiveCodes.has(CodigoSup1)) blocked.add(CodigoSup2);
        if (allActiveCodes.has(CodigoSup2)) blocked.add(CodigoSup1);
      },
    );
    return blocked;
  }, [allActiveCodes, selectedProductId]);

  const availableColoresOptions = useMemo(
    () =>
      coloresOptions.filter(
        (o) => o.value === "" || !blockedCodes.has(o.value),
      ),
    [coloresOptions, blockedCodes],
  );

  const requiereTratamiento = useMemo(() => {
    if (selectedProductId) {
      const lente = catalogueData?.Lentes.find(
        (l) => l.Producto.CodigoProducto === selectedProductId,
      );
      if (lente?.Producto.RequiereTratamiento) return true;
    }
    if (colorSel?.value) {
      const requiereViaColor = (catalogueData?.Incompatibilidades ?? []).some(
        ({ CodigoSup1, CodigoSup2, CodigosProducto }) =>
          CodigoSup1 === colorSel.value &&
          CodigoSup2 === "" &&
          (CodigosProducto.length === 0 ||
            (selectedProductId && CodigosProducto.includes(selectedProductId))),
      );
      if (requiereViaColor) return true;
    }
    return false;
  }, [selectedProductId, colorSel]);

  const availableTratamientosOptions = useMemo(
    () =>
      tratamientosOptions.filter(
        (o) => o.value === "" || !blockedCodes.has(o.value),
      ),
    [tratamientosOptions, blockedCodes],
  );

  const availableInstruccionesEspecialesOptions = useMemo(() => {
    const usedCodes = new Set(instruccionesEspecialesTable.map((i) => i.id));
    return instruccionesEspecialesOptions.filter(
      (o) =>
        o.value === "" ||
        (!usedCodes.has(o.value) && !blockedCodes.has(o.value)),
    );
  }, [
    instruccionesEspecialesOptions,
    instruccionesEspecialesTable,
    blockedCodes,
  ]);

  const insertInstruccionEspecial = (parameters = []) => {
    setInstruccionesEspecialesTable([
      ...instruccionesEspecialesTable,
      {
        id: instruccionEspecialSel.value,
        codigoSuplemento: instruccionEspecialSel.value,
        nombre: instruccionEspecialSel.nombre,
        pvp: instruccionEspecialSel.pvp,
        parameters,
      },
    ]);
    setInstruccionEspecialSel({ value: "", text: "" });
  };

  const addInstruccionEspecial = () => {
    if (!instruccionEspecialSel?.value) return;
    if (
      instruccionesEspecialesTable.some(
        (i) => i.id === instruccionEspecialSel.value,
      )
    )
      return;
    const parametros =
      catalogueData?.Parametros?.find(
        (p) => p.CodigoTipParam === instruccionEspecialSel.value,
      )?.Parametros ?? [];
    if (parametros.length === 0) {
      insertInstruccionEspecial();
      return;
    }
    setParametrosPopup(parametros);
    setShowParametersPopup(true);
  };

  const confirmAddInstruccionEspecial = (parameters) => {
    insertInstruccionEspecial(parameters);
    setShowParametersPopup(false);
  };

  const cancelAddInstruccionEspecial = () => {
    setShowParametersPopup(false);
  };

  const removeInstruccionEspecial = (id) => {
    setInstruccionesEspecialesTable(
      instruccionesEspecialesTable.filter((i) => i.id !== id),
    );
  };

  const availableInstruccionesMontajeOptions = useMemo(() => {
    const usedCodes = new Set(instruccionesMontajeTable.map((i) => i.id));
    return instruccionesMontajeOptions.filter(
      (o) =>
        o.value === "" ||
        (!usedCodes.has(o.value) && !blockedCodes.has(o.value)),
    );
  }, [instruccionesMontajeOptions, instruccionesMontajeTable, blockedCodes]);

  const addInstruccionMontaje = () => {
    if (!instruccionMontajeSel?.value) return;
    if (
      instruccionesMontajeTable.some(
        (i) => i.id === instruccionMontajeSel.value,
      )
    )
      return;
    setInstruccionesMontajeTable([
      ...instruccionesMontajeTable,
      {
        id: instruccionMontajeSel.value,
        codigoSuplemento: instruccionMontajeSel.value,
        nombre: instruccionMontajeSel.nombre,
        pvp: instruccionMontajeSel.pvp,
      },
    ]);
    setInstruccionMontajeSel({ value: "", text: "" });
  };

  const removeInstruccionMontaje = (id) => {
    setInstruccionesMontajeTable(
      instruccionesMontajeTable.filter((i) => i.id !== id),
    );
  };

  const instruccionesMontajeColumns = useMemo(
    () => [
      {
        name: "Código",
        selector: (row) => row.codigoSuplemento,
        sortable: true,
        width: "100px",
      },
      {
        name: "Nombre",
        selector: (row) => row.nombre?.toUpperCase(),
        sortable: true,
      },
      {
        name: "PVP",
        selector: (row) => formatCurrency(row.pvp),
        sortable: true,
        sortFunction: (a, b) => a.pvp - b.pvp,
        right: true,
        width: "100px",
      },
      {
        name: "",
        width: "50px",
        cell: (row) => (
          <div className="navtable-opt ntc-trash cash-voucher-popup">
            <img
              src="/images/remove-blue.svg"
              className="c-pointer"
              onClick={() => removeInstruccionMontaje(row.id)}
            />
          </div>
        ),
      },
    ],
    [instruccionesMontajeTable],
  );

  const instruccionesEspecialesColumns = useMemo(
    () => [
      {
        name: "Código",
        selector: (row) => row.codigoSuplemento,
        sortable: true,
        width: "100px",
        cell: (row) => (
          <span
            className="catalog-parametros-tooltip-cell"
            data-tooltip-id={row.parameters?.length > 0 ? String(row.id) : undefined}
          >
            {row.codigoSuplemento}
          </span>
        ),
      },
      {
        name: "Nombre",
        selector: (row) => row.nombre?.toUpperCase(),
        sortable: true,
        cell: (row) => (
          <>
            <span
              className="catalog-parametros-tooltip-cell"
              data-tooltip-id={row.parameters?.length > 0 ? String(row.id) : undefined}
            >
              {row.nombre?.toUpperCase()}
            </span>
            {row.parameters?.length > 0 && (
              <TooltipBox
                id={String(row.id)}
                content={
                  <div className="catalog-parametros-tooltip">
                    {row.parameters.map((parametro, index) => (
                      <div key={index}>
                        {parametro.CodigoEdi} - {parametro.Nombre}:{" "}
                        {parametro.Valor} {parametro.UnidadesMedida}
                      </div>
                    ))}
                  </div>
                }
              />
            )}
          </>
        ),
      },
      {
        name: "PVP",
        selector: (row) => formatCurrency(row.pvp),
        sortable: true,
        sortFunction: (a, b) => a.pvp - b.pvp,
        right: true,
        width: "100px",
        cell: (row) => (
          <span
            className="catalog-parametros-tooltip-cell catalog-parametros-tooltip-cell-right"
            data-tooltip-id={row.parameters?.length > 0 ? String(row.id) : undefined}
          >
            {formatCurrency(row.pvp)}
          </span>
        ),
      },
      {
        name: "",
        width: "50px",
        cell: (row) => (
          <div className="navtable-opt ntc-trash cash-voucher-popup">
            <img
              src="/images/remove-blue.svg"
              className="c-pointer"
              onClick={() => removeInstruccionEspecial(row.id)}
            />
          </div>
        ),
      },
    ],
    [instruccionesEspecialesTable],
  );

  const productsColumns = useMemo(
    () => [
      {
        name: "Código",
        selector: (row) => row.codigoProducto,
        sortable: true,
        width: "20%",
      },
      {
        name: "Nombre",
        selector: (row) => row.nombre?.toUpperCase(),
        sortable: true,
        grow: 1,
      },
      {
        name: "Índ",
        selector: (row) => String(row.indice).replace(".", ","),
        sortable: true,
        sortFunction: (a, b) => a.indice - b.indice,
        width: "10%",
        right: true,
      },
      {
        name: "Den",
        selector: (row) => String(row.densidad).replace(".", ","),
        sortable: true,
        sortFunction: (a, b) => a.densidad - b.densidad,
        width: "10%",
        right: true,
      },
      {
        name: "",
        sortable: false,
        width: "6%",
        cell: (row) => (
          <div
            className="data-field c-pointer"
            onClick={(e) => {
              e.stopPropagation();
              selectProduct(row.id);
            }}
          >
            <CheckBox active={row.selected} onClick={() => {}} />
          </div>
        ),
      },
    ],
    [productsTable],
  );

  return (
    show && (
      <>
        <div className="popup-container popup-normal popup-central-catalog">
        <div className="popup-message">
          <div className="popup-header">
            <div className="popup-title">Catálogo central</div>
            <img
              src="/images/close-white.svg"
              loading="lazy"
              alt=""
              className="popup-close"
              onClick={onClose}
            />
          </div>
          <div className="popup-body">
            <div className="popup-container-block">
              <span className="section-content-title">Proveedores</span>
              <div className="section-content-sep" />
              <div className="popup-row">
                {CENTRAL_CATALOG_PROVIDERS.map((provider) => (
                  <CentralCatalogProvider
                    key={provider.id}
                    logo={provider.logo}
                    text={providerDates[provider.id] ?? ""}
                    enabled={selectedProvider === provider.id}
                    onClick={() => {
                      if (!catalogueData) loadCatalogue();
                      setSelectedProvider(provider.id);
                    }}
                  />
                ))}
              </div>
            </div>
            {selectedProvider && (
              <div className="catalog-main-row">
                <div className="popup-container-block" ref={productsColumnRef}>
                  <span className="section-content-title">
                    Productos ({filteredProductsTable.length} de{" "}
                    <span className="listado-total">
                      {productsTable.length}
                    </span>
                    )
                  </span>
                  <div className="section-content-sep" />
                  <div className="catalog-products-layout">
                    <div className="catalog-filters-col">
                      <div className="popup-row">
                        <div className="label">Nombre</div>
                        <InputData
                          id="catalog-nombre-filter"
                          type="type-alphanum-ext"
                          size={6}
                          maxLength={50}
                          input={nombreFilter}
                          onChange={(e) => setNombreFilter(e)}
                        />
                      </div>
                      <div className="popup-row">
                        <div className="label">Tipo</div>
                        <Dropdown
                          id="catalog-tipo-filter"
                          optionList={tipoOptions}
                          size={0}
                          op={tipoFilterSel?.value}
                          onChange={(e) => setTipoFilterSel(e)}
                        />
                      </div>
                      <div className="popup-row">
                        <div className="label">Tipo de lente</div>
                        <Dropdown
                          id="catalog-tipo-lente-filter"
                          optionList={tipoLenteOptions}
                          size={0}
                          op={tipoLenteFilterSel?.value}
                          onChange={(e) => setTipoLenteFilterSel(e)}
                        />
                      </div>
                      <div className="popup-row">
                        <div className="label">Material</div>
                        <Dropdown
                          id="catalog-material-filter"
                          optionList={materialOptions}
                          size={0}
                          op={materialFilterSel?.value}
                          onChange={(e) => setMaterialFilterSel(e)}
                        />
                      </div>
                      <div className="popup-row">
                        <div className="label">Geometría</div>
                        <Dropdown
                          id="catalog-geometria-filter"
                          optionList={geometriaOptions}
                          size={0}
                          op={geometriaFilterSel?.value}
                          onChange={(e) => setGeometriaFilterSel(e)}
                        />
                      </div>
                      <div className="popup-row">
                        <div className="label">Masa</div>
                        <Dropdown
                          id="catalog-masa-filter"
                          optionList={masaOptions}
                          size={0}
                          op={masaFilterSel?.value}
                          onChange={(e) => setMasaFilterSel(e)}
                        />
                      </div>
                      <div className="popup-row catalog-row-range">
                        <div className="label">Índ (Desde)</div>
                        <InputData
                          id="catalog-ind-desde"
                          type="type-float"
                          size={4}
                          maxLength={15}
                          input={indDesde}
                          onChange={(e) => setIndDesde(e)}
                        />
                        <div className="label">Índ (Hasta)</div>
                        <InputData
                          id="catalog-ind-hasta"
                          type="type-float"
                          size={4}
                          maxLength={15}
                          input={indHasta}
                          onChange={(e) => setIndHasta(e)}
                        />
                      </div>
                      <div className="popup-row catalog-row-range">
                        <div className="label">Den (Desde)</div>
                        <InputData
                          id="catalog-den-desde"
                          type="type-float"
                          size={4}
                          maxLength={15}
                          input={denDesde}
                          onChange={(e) => setDenDesde(e)}
                        />
                        <div className="label">Den (Hasta)</div>
                        <InputData
                          id="catalog-den-hasta"
                          type="type-float"
                          size={4}
                          maxLength={15}
                          input={denHasta}
                          onChange={(e) => setDenHasta(e)}
                        />
                      </div>
                    </div>
                    <div className="catalog-products-table">
                      <DataTable
                        columns={productsColumns}
                        data={filteredProductsTable}
                        className="custom-datatable"
                        noDataComponent="No hay datos disponibles."
                        onRowClicked={(row) => selectProduct(row.id)}
                      />
                    </div>
                  </div>
                  <span className="section-content-title">
                    Lentes ({filteredLentesTable.length} de{" "}
                    <span className="listado-total">{lentesTable.length}</span>)
                  </span>
                  <div className="section-content-sep" />
                  <div className="catalog-products-layout">
                    <div className="catalog-filters-col catalog-lentes-filters">
                      <div className="popup-row">
                        <div className="label">Diá</div>
                        <Dropdown
                          id="lente-dia"
                          optionList={diaOptions}
                          size={0}
                          op={diaDesde?.value}
                          onChange={(e) => setDiaDesde(e)}
                          mandatory
                        />
                      </div>
                      <div className="popup-row">
                        <div className="label">Cil</div>
                        <InputDataControl
                          id="lente-cil"
                          type="type-optofloat"
                          size={6}
                          maxLength={15}
                          input={cilDesde}
                          minValue=""
                          maxValue=""
                          step={0.25}
                          numDecimales=""
                          createPopupQuestion={createPopupQuestion}
                          removePopupQuestion={removePopupQuestion}
                          onChange={(e) => setCilDesde(e)}
                        />
                      </div>
                      <div className="popup-row">
                        <div className="label">Esf</div>
                        <InputDataControl
                          id="lente-esf"
                          type="type-optofloat"
                          size={6}
                          maxLength={15}
                          input={esfDesde}
                          minValue=""
                          maxValue=""
                          step={0.25}
                          numDecimales=""
                          createPopupQuestion={createPopupQuestion}
                          removePopupQuestion={removePopupQuestion}
                          onChange={(e) => setEsfDesde(e)}
                        />
                      </div>
                      <div className="popup-row">
                        <div className="label">Add</div>
                        <InputDataControl
                          id="lente-adi"
                          type="type-optofloat"
                          size={6}
                          maxLength={15}
                          input={adiSel}
                          minValue=""
                          maxValue=""
                          step={0.25}
                          numDecimales=""
                          createPopupQuestion={createPopupQuestion}
                          removePopupQuestion={removePopupQuestion}
                          onChange={(e) => setAdiSel(e)}
                        />
                      </div>
                    </div>
                    <div className="catalog-products-table catalog-lentes-table">
                      <DataTable
                        columns={lentesColumns}
                        data={filteredLentesTable}
                        className="custom-datatable"
                        noDataComponent={
                          lentesTable.length === 0
                            ? "Selecciona un producto para mostrar la configuración de la lente."
                            : "No se encuentran lentes."
                        }
                        onRowClicked={(row) => selectLente(row.id)}
                      />
                    </div>
                  </div>
                </div>
                <div
                  className="catalog-tratamientos-col"
                  style={
                    suplementosColumnHeight
                      ? { height: suplementosColumnHeight }
                      : undefined
                  }
                >
                  <div className="popup-container-block catalog-tratamientos-block">
                    <span className="section-content-title">Suplementos</span>
                    <div className="section-content-sep" />
                    <div className="popup-row">
                      <div className="label">Color</div>
                      <Dropdown
                        key={`color-${selectedProductId ?? "none"}`}
                        id="catalog-color"
                        optionList={availableColoresOptions}
                        size={0}
                        op={colorSel?.value}
                        onChange={(e) => setColorSel(e)}
                        disabled={lentesTable.length === 0}
                      />
                    </div>
                    <div className="popup-row">
                      <div className="label">
                        Tratamiento{requiereTratamiento ? " (*)" : ""}
                      </div>
                      <Dropdown
                        key={`tratamiento-${selectedProductId ?? "none"}`}
                        id="catalog-tratamiento"
                        optionList={availableTratamientosOptions}
                        size={0}
                        op={tratamientoSel?.value}
                        onChange={(e) => setTratamientoSel(e)}
                        disabled={lentesTable.length === 0}
                      />
                    </div>
                    <div className="popup-row">
                      <div className="label">
                        Inst. Especiales ({instruccionesEspecialesTable.length})
                      </div>
                      <Dropdown
                        key={`inst-especiales-${selectedProductId ?? "none"}-${instruccionesEspecialesTable.length}`}
                        id="catalog-inst-especiales"
                        optionList={availableInstruccionesEspecialesOptions}
                        size={0}
                        op={instruccionEspecialSel?.value}
                        onChange={(e) => setInstruccionEspecialSel(e)}
                        disabled={lentesTable.length === 0}
                      />
                      <img
                        src="/images/load.svg"
                        loading="lazy"
                        width="22"
                        height="22"
                        alt="load"
                        className="vectors-wrapper-76 c-pointer"
                        onClick={addInstruccionEspecial}
                      />
                    </div>
                    <div className="catalog-products-table catalog-tratamientos-table">
                      <DataTable
                        columns={instruccionesEspecialesColumns}
                        data={instruccionesEspecialesTable}
                        className="custom-datatable"
                        noDataComponent={
                          lentesTable.length === 0
                            ? "Selecciona un producto para mostrar las instrucciones especiales."
                            : "No se han añadido instrucciones especiales."
                        }
                      />
                    </div>
                    <div className="popup-row">
                      <div className="label">
                        Inst. Montaje ({instruccionesMontajeTable.length})
                      </div>
                      <Dropdown
                        key={`inst-montaje-${selectedProductId ?? "none"}-${instruccionesMontajeTable.length}`}
                        id="catalog-inst-montaje"
                        optionList={availableInstruccionesMontajeOptions}
                        size={0}
                        op={instruccionMontajeSel?.value}
                        onChange={(e) => setInstruccionMontajeSel(e)}
                        disabled={lentesTable.length === 0}
                      />
                      <img
                        src="/images/load.svg"
                        loading="lazy"
                        width="22"
                        height="22"
                        alt="load"
                        className="vectors-wrapper-76 c-pointer"
                        onClick={addInstruccionMontaje}
                      />
                    </div>
                    <div className="catalog-products-table catalog-tratamientos-table">
                      <DataTable
                        columns={instruccionesMontajeColumns}
                        data={instruccionesMontajeTable}
                        className="custom-datatable"
                        noDataComponent={
                          lentesTable.length === 0
                            ? "Selecciona un producto para mostrar las instrucciones de montaje."
                            : "No se han añadido instrucciones de montaje."
                        }
                      />
                    </div>
                  </div>
                  <div className="popup-container-block catalog-totales-block">
                    <div className="catalog-totales-section">
                      <div className="label">Total Lentes</div>
                      <InputData
                        type="type-alpha"
                        size={1}
                        input={formatCurrency(totalLentes)}
                        onChange={null}
                        inactive={true}
                      />
                      <div className="label">Total Supl.</div>
                      <InputData
                        type="type-alpha"
                        size={1}
                        input={formatCurrency(totalTratamientos)}
                        onChange={null}
                        inactive={true}
                      />
                      <div className="label catalog-totales-label-total">
                        Total
                      </div>
                      <InputData
                        type="type-alpha"
                        size={1}
                        input={formatCurrency(total)}
                        onChange={null}
                        inactive={true}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className="popup-row-buttons">
              <ButtonIconH
                text="Cargar para ambos ojos"
                disabled={
                  !lentesTable.some((l) => l.selected) ||
                  (requiereTratamiento && !tratamientoSel?.value)
                }
                onClick={() => {
                  const selectedProduct = productsTable.find((p) => p.selected);
                  const selectedLente = lentesTable.find((l) => l.selected);
                  const fullLente = catalogueData?.Lentes.find(
                    (l) =>
                      l.Producto.CodigoProducto ===
                      selectedProduct?.codigoProducto,
                  );
                  onCargar({
                    ojo: "AMBOS",
                    provider: (selectedProvider ?? "").toUpperCase(),
                    codigoProducto: selectedProduct?.codigoProducto ?? "",
                    descripcionProducto: (
                      selectedProduct?.nombre ?? ""
                    ).toUpperCase(),
                    ind: selectedProduct?.indice ?? "",
                    den: selectedProduct?.densidad ?? "",
                    tipo: selectedProduct?.tipo ?? "",
                    tipoLente: fullLente?.Producto.TipoLente ?? null,
                    material: fullLente?.Producto.Material ?? null,
                    geometria: fullLente?.Producto.Geometria ?? null,
                    masa: fullLente?.Producto.Masa ?? null,
                    dia: diaDesde?.value || selectedLente?.diametro || "",
                    cil: cilDesde,
                    esf: esfDesde,
                    add: adiSel,
                    color: colorSel?.value
                      ? {
                          codigo: colorSel.value,
                          Descripcion: (colorSel.nombre ?? "").toUpperCase(),
                        }
                      : null,
                    tratamiento: tratamientoSel?.value
                      ? {
                          codigo: tratamientoSel.value,
                          texto: (tratamientoSel.nombre ?? "").toUpperCase(),
                        }
                      : null,
                    instEspeciales: instruccionesEspecialesTable.map((i) => ({
                      codigo: i.codigoSuplemento,
                      texto: i.nombre.toUpperCase(),
                      parametros: (i.parameters ?? []).map((p) => ({
                        codigoEdi: p.CodigoEdi,
                        nombre: p.Nombre,
                        valor: p.Valor,
                        medida: p.UnidadesMedida,
                      })),
                    })),
                    instMontaje: instruccionesMontajeTable.map((i) => ({
                      codigo: i.codigoSuplemento,
                      texto: i.nombre.toUpperCase(),
                    })),
                    totalLentes,
                    totalSuplementos: totalTratamientos,
                  });
                  onClose();
                }}
              />
              <ButtonIconH
                text={`Cargar sólo para ${ojo}`}
                disabled={
                  !lentesTable.some((l) => l.selected) ||
                  (requiereTratamiento && !tratamientoSel?.value)
                }
                onClick={() => {
                  const selectedProduct = productsTable.find((p) => p.selected);
                  const selectedLente = lentesTable.find((l) => l.selected);
                  const fullLente = catalogueData?.Lentes.find(
                    (l) =>
                      l.Producto.CodigoProducto ===
                      selectedProduct?.codigoProducto,
                  );
                  onCargar({
                    ojo,
                    provider: (selectedProvider ?? "").toUpperCase(),
                    codigoProducto: selectedProduct?.codigoProducto ?? "",
                    descripcionProducto: (
                      selectedProduct?.nombre ?? ""
                    ).toUpperCase(),
                    ind: selectedProduct?.indice ?? "",
                    den: selectedProduct?.densidad ?? "",
                    tipo: selectedProduct?.tipo ?? "",
                    tipoLente: fullLente?.Producto.TipoLente ?? null,
                    material: fullLente?.Producto.Material ?? null,
                    geometria: fullLente?.Producto.Geometria ?? null,
                    masa: fullLente?.Producto.Masa ?? null,
                    dia: diaDesde?.value || selectedLente?.diametro || "",
                    cil: cilDesde,
                    esf: esfDesde,
                    add: adiSel,
                    color: colorSel?.value
                      ? {
                          codigo: colorSel.value,
                          Descripcion: (colorSel.nombre ?? "").toUpperCase(),
                        }
                      : null,
                    tratamiento: tratamientoSel?.value
                      ? {
                          codigo: tratamientoSel.value,
                          texto: (tratamientoSel.nombre ?? "").toUpperCase(),
                        }
                      : null,
                    instEspeciales: instruccionesEspecialesTable.map((i) => ({
                      codigo: i.codigoSuplemento,
                      texto: i.nombre.toUpperCase(),
                      parametros: (i.parameters ?? []).map((p) => ({
                        codigoEdi: p.CodigoEdi,
                        nombre: p.Nombre,
                        valor: p.Valor,
                        medida: p.UnidadesMedida,
                      })),
                    })),
                    instMontaje: instruccionesMontajeTable.map((i) => ({
                      codigo: i.codigoSuplemento,
                      texto: i.nombre.toUpperCase(),
                    })),
                    totalLentes,
                    totalSuplementos: totalTratamientos,
                  });
                  onClose();
                }}
              />
            </div>
          </div>
        </div>
        </div>
        <CentralCatalogParametersPopup
          show={showParametersPopup}
          parametros={parametrosPopup}
          onAccept={confirmAddInstruccionEspecial}
          onCancel={cancelAddInstruccionEspecial}
          createPopupQuestion={createPopupQuestion}
          removePopupQuestion={removePopupQuestion}
        />
      </>
    )
  );
};
